import { useState, useEffect, useRef } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import api from '../../services/api'
import ActForConsultant from './ActForConsultant.jsx'
import { useAuth } from '../../hooks/useAuth'
import { ProjectsByStatusPie, BudgetUtilizationBar } from '../common/DashboardCharts.jsx'
import { Users, Search, X, ShieldCheck, ShieldOff, Loader2, FolderKanban, CheckCircle2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'

const ROLE_TABS = ['All', 'Consultant', 'Project Manager', 'Finance', 'Funder', 'HR', 'Partner']

/**
 * Admin master panel — per-role overview of every stakeholder + access control.
 * Admin picks a role, sees everyone, opens a person for their 360° view (projects,
 * progress, charts), and can activate/suspend or change a role inline.
 */
export default function PeopleManagement() {
  const { t } = useTranslation('people')
  const { user } = useAuth()
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [tab, setTab] = useState('All')
  const [search, setSearch] = useState('')
  const [busyId, setBusyId] = useState(null)
  const [error, setError] = useState(null)

  // Detail panel
  const [openId, setOpenId] = useState(null)
  const [detail, setDetail] = useState(null)
  const [detailLoading, setDetailLoading] = useState(false)

  const isAdmin = user?.role === 'Admin'
  // Admins, or anyone granted the manage_users capability, may manage people + grant access.
  const canManage = isAdmin || (user?.capabilities || []).includes('manage_users')

  // Capability grant panel
  const [capsFor, setCapsFor] = useState(null)   // the user whose access is being edited
  const [capsData, setCapsData] = useState(null) // {granted, resolved, catalog}
  const [capsBusy, setCapsBusy] = useState(false)

  const openCaps = async (u) => {
    setCapsFor(u); setCapsData(null)
    try {
      const r = await api.get(`/admin/users/${u.id}/capabilities`)
      setCapsData(r.data)
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load access')
      setCapsFor(null)
    }
  }
  const saveCaps = async (next) => {
    setCapsBusy(true); setError(null)
    try {
      const r = await api.put(`/admin/users/${capsFor.id}/capabilities`, { capabilities: next })
      setCapsData(d => ({ ...d, granted: r.data.granted, resolved: r.data.resolved }))
    } catch (e) {
      setError(e.response?.data?.detail || 'Update failed')
    } finally {
      setCapsBusy(false)
    }
  }
  const toggleCap = (slug) => {
    if (!capsData) return
    const granted = new Set(capsData.granted || [])
    granted.has(slug) ? granted.delete(slug) : granted.add(slug)
    saveCaps(Array.from(granted))
  }
  // Tiered-PM presets (v2 Theme 2.3): quick capability bundles on top of the PM role.
  const CAP_PRESETS = {
    'Level-1 PM': ['manage_all_projects', 'onboard_users', 'approve_invoices'],
    'Level-2 PM (standard)': [],
  }

  const load = () => {
    setLoading(true)
    api.get('/users/')
      .then(r => setUsers(Array.isArray(r.data) ? r.data : []))
      .catch(() => setError('Failed to load people'))
      .finally(() => setLoading(false))
  }
  useEffect(() => { load() }, [])

  // Arriving from the Consultants page with ?user=<id> — open that person straight away.
  // Landing on the list and making somebody search for the name they just clicked is the
  // same dead end this link was added to remove.
  const [params, setParams] = useSearchParams()
  const navigate = useNavigate()
  // Where closing the panel should go. Set only when they arrived by deep link: somebody who
  // opened it from this list belongs back on this list, but somebody who came from Consultants
  // was left standing on a User Management screen they never asked for, with no way back.
  const returnTo = useRef(null)
  useEffect(() => {
    const id = params.get('user')
    if (id) {
      returnTo.current = params.get('from') || null
      openPerson(Number(id))
      setParams(prev => {
        const n = new URLSearchParams(prev); n.delete('user'); n.delete('from'); return n
      }, { replace: true })
    }
  }, [])

  // One function, so the overlay click and the X cannot disagree about where closing goes.
  const closePanel = () => {
    const back = returnTo.current
    returnTo.current = null
    setOpenId(null)
    if (back) navigate(back)
  }

  const openPerson = async (id) => {
    setOpenId(id); setDetail(null); setDetailLoading(true)
    try {
      const r = await api.get(`/admin/people/${id}/overview`)
      setDetail(r.data)
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load overview')
    } finally {
      setDetailLoading(false)
    }
  }

  const updateUser = async (id, patch) => {
    setBusyId(id); setError(null)
    try {
      await api.patch(`/admin/users/${id}`, patch)
      load()
      if (openId === id) openPerson(id)
    } catch (e) {
      setError(e.response?.data?.detail || 'Update failed')
    } finally {
      setBusyId(null)
    }
  }

  if (!canManage) return <div className="p-6 text-gray-500">{t('no_perm_people')}</div>

  const filtered = users.filter(u => {
    const roleOk = tab === 'All' || u.role === tab
    const q = search.toLowerCase()
    const searchOk = !q || (u.full_name || '').toLowerCase().includes(q) ||
      (u.email || '').toLowerCase().includes(q) || (u.consultant_code || '').toLowerCase().includes(q)
    return roleOk && searchOk
  })

  const roleCount = (r) => users.filter(u => r === 'All' || u.role === r).length

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="bg-linear-to-r from-purple-600 to-indigo-600 rounded-xl p-6 text-white">
        <h1 className="text-2xl font-bold flex items-center gap-2"><Users className="w-6 h-6" /> People & Access</h1>
        <p className="mt-1 opacity-90 text-sm">Overview every stakeholder, see their work, and control access.</p>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">{error}</div>}

      {/* Role tabs */}
      <div className="flex flex-wrap gap-2">
        {ROLE_TABS.map(r => (
          <button
            key={r}
            onClick={() => setTab(r)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              tab === r ? 'bg-indigo-600 text-white' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'
            }`}
          >
            {r} <span className={`ml-1 text-xs ${tab === r ? 'text-indigo-200' : 'text-gray-400'}`}>{roleCount(r)}</span>
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder={t('ph_search_people')}
          className="w-full border border-gray-300 rounded-lg pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
        />
      </div>

      {/* People list */}
      {loading ? (
        <div className="py-12 text-center text-gray-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
      ) : filtered.length === 0 ? (
        <div className="py-12 text-center text-gray-400">{t('no_people_found')}</div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-100">
          {filtered.map(u => (
            <div key={u.id} className="flex flex-col sm:flex-row sm:items-center gap-3 px-5 py-3">
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className="w-10 h-10 rounded-full bg-indigo-600 text-white flex items-center justify-center font-bold shrink-0">
                  {(u.full_name || '?').charAt(0).toUpperCase()}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-900 truncate">{u.full_name}</p>
                  <p className="text-xs text-gray-500 truncate">
                    {u.consultant_code ? <span className="font-mono">{u.consultant_code} · </span> : ''}
                    {u.email || '—'}
                  </p>
                </div>
              </div>

              {/* Role select */}
              <select
                value={u.role}
                disabled={busyId === u.id || u.id === user.id}
                onChange={(e) => updateUser(u.id, { role: e.target.value })}
                className="text-xs border border-gray-200 rounded-md px-2 py-1.5 bg-white disabled:opacity-50"
                title={u.id === user.id ? "You can't change your own role" : 'Change role'}
              >
                {['Admin', 'Project Manager', 'Finance', 'Funder', 'Consultant', 'HR'].map(r => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>

              {/* Master/Default PM toggle — only meaningful for a Project Manager */}
              {u.role === 'Project Manager' && (
                <button
                  onClick={() => updateUser(u.id, { is_primary_pm: !u.is_primary_pm })}
                  disabled={busyId === u.id}
                  className={`flex items-center gap-1 text-xs px-3 py-1.5 rounded-md font-medium disabled:opacity-50 ${
                    u.is_primary_pm ? 'bg-purple-100 text-purple-700 hover:bg-purple-200' : 'bg-gray-50 text-gray-500 hover:bg-gray-100'
                  }`}
                  title={u.is_primary_pm ? 'Revoke Master PM (sees/manages every project)' : 'Grant Master PM — sees/manages every project'}
                >
                  {u.is_primary_pm ? '★ Master PM' : 'Make Master PM'}
                </button>
              )}

              {/* Active / suspend toggle */}
              <button
                onClick={() => updateUser(u.id, { is_active: !u.is_active })}
                disabled={busyId === u.id || u.id === user.id}
                className={`flex items-center gap-1 text-xs px-3 py-1.5 rounded-md font-medium disabled:opacity-50 ${
                  u.is_active ? 'bg-green-50 text-green-700 hover:bg-green-100' : 'bg-red-50 text-red-700 hover:bg-red-100'
                }`}
                title={u.id === user.id ? "You can't suspend yourself" : (u.is_active ? 'Suspend login' : 'Reactivate login')}
              >
                {u.is_active ? <><ShieldCheck className="w-3.5 h-3.5" /> Active</> : <><ShieldOff className="w-3.5 h-3.5" /> Suspended</>}
              </button>

              {/* Delegated access (capabilities) — not shown for Admins (they have everything) */}
              {u.role !== 'Admin' && (
                <button
                  onClick={() => openCaps(u)}
                  className="text-xs px-3 py-1.5 rounded-md border border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100"
                  title={t('t_grant_caps')}
                >
                  🔑 Access
                </button>
              )}

              <button
                onClick={() => openPerson(u.id)}
                className="text-xs px-3 py-1.5 rounded-md border border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100"
              >
                View
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Capability grant modal */}
      {capsFor && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setCapsFor(null)}>
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full" onClick={e => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold text-gray-900">Access for {capsFor.full_name}</h2>
                <p className="text-xs text-gray-500">{capsFor.role} · toggle admin-style capabilities on top of their role</p>
              </div>
              <button onClick={() => setCapsFor(null)} className="p-1.5 text-gray-400 hover:text-gray-700 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <div className="px-5 py-4">
              {!capsData ? (
                <div className="flex items-center gap-2 text-sm text-gray-500 py-6 justify-center"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
              ) : (
                <div className="space-y-2">
                  {capsFor.role === 'Project Manager' && (
                    <div className="flex flex-wrap items-center gap-2 pb-2 mb-1 border-b border-gray-100">
                      <span className="text-[11px] text-gray-500">{t('presets')}</span>
                      {Object.entries(CAP_PRESETS).map(([label, slugs]) => (
                        <button key={label} onClick={() => saveCaps(slugs)} disabled={capsBusy}
                          className="text-[11px] px-2 py-1 rounded-md border border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 disabled:opacity-50">
                          {label}
                        </button>
                      ))}
                    </div>
                  )}
                  {Object.entries(capsData.catalog).map(([slug, label]) => {
                    const on = (capsData.granted || []).includes(slug)
                    return (
                      <div key={slug} className="flex items-center justify-between gap-3 border border-gray-100 rounded-lg px-3 py-2">
                        <span className="text-sm text-gray-800">{label}</span>
                        <button
                          onClick={() => toggleCap(slug)}
                          disabled={capsBusy}
                          className={`shrink-0 px-3 py-1 text-[11px] font-semibold rounded-full transition-colors disabled:opacity-50 ${
                            on ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                          }`}
                        >
                          {on ? 'On' : 'Off'}
                        </button>
                      </div>
                    )
                  })}
                  <p className="text-[11px] text-gray-400 pt-1">Granting a capability gives this user that admin feature while keeping their normal role. "Manage people & grant access" lets them grant access to others too.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Detail modal */}
      {openId && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={closePanel}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 bg-gray-50 rounded-t-2xl sticky top-0">
              <h2 className="font-semibold text-gray-800">{t('stakeholder_overview')}</h2>
              <button onClick={closePanel} className="p-1.5 text-gray-400 hover:text-gray-700"><X className="w-5 h-5" /></button>
            </div>

            <div className="p-6">
              {detailLoading || !detail ? (
                <div className="py-12 text-center"><Loader2 className="w-6 h-6 animate-spin text-indigo-600 mx-auto" /></div>
              ) : (
                <div className="space-y-6">
                  {/* Profile */}
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-full bg-indigo-600 text-white flex items-center justify-center font-bold text-xl">
                      {(detail.user.full_name || '?').charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">{detail.user.full_name}</h3>
                      <p className="text-sm text-gray-500">
                        {detail.user.role}
                        {detail.user.consultant_code ? <span className="font-mono"> · {detail.user.consultant_code}</span> : ''}
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`text-[11px] px-2 py-0.5 rounded-full ${detail.user.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                          {detail.user.is_active ? 'Active' : 'Suspended'}
                        </span>
                        {detail.user.has_id_document && (
                          <span className="text-[11px] px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 flex items-center gap-1">
                            <CheckCircle2 className="w-3 h-3" /> ID: {detail.user.id_document_type || 'on file'}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* What is still missing — the first thing an admin wants when they open
                      somebody's record, and previously the one thing nowhere on the screen. */}
                  {detail.outstanding?.length > 0 && (
                    <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
                      <p className="text-xs font-bold text-amber-900 uppercase tracking-wide mb-1.5">
                        Still outstanding ({detail.outstanding.length})
                      </p>
                      <ul className="text-sm text-amber-900 space-y-1">
                        {detail.outstanding.map((o, i) => (
                          <li key={i} className="flex gap-2"><span>&bull;</span><span>{o}</span></li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Right below what is missing, because that list is where an admin realises
                      the consultant cannot supply it themselves. Consultants only — the
                      endpoints refuse anyone else, so this is convenience, not the guard. */}
                  {canManage && detail.user.role === 'Consultant' && (
                    <ActForConsultant
                      userId={detail.user.id}
                      userName={detail.user.full_name || 'this consultant'}
                      person={detail.user}
                      onDone={() => openPerson(detail.user.id)}
                    />
                  )}

                  {/* Contact + banking. Account numbers are never returned in full — the last
                      four digits answer "are these the details I expect?" without making this
                      a screen somebody could harvest. */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="rounded-xl border border-gray-200 px-4 py-3">
                      <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">{t('contact')}</p>
                      <dl className="text-sm space-y-1">
                        <Row k="Email" v={detail.user.email} />
                        <Row k="Phone" v={detail.user.phone_number} private_ />
                        <Row k="LinkedIn" v={detail.user.linkedin_url ? (
                          <a href={detail.user.linkedin_url} target="_blank" rel="noreferrer"
                             className="text-blue-600 hover:underline">{t('open_profile')}</a>
                        ) : null} />
                        <Row k="Specialization" v={detail.user.specialization} />
                        <Row k="CV" v={detail.user.has_cv ? 'On file' : null} />
                      </dl>
                    </div>

                    <div className="rounded-xl border border-gray-200 px-4 py-3">
                      <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                        Bank details
                        {detail.banking && (
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                            detail.banking.verified ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-800'}`}>
                            {detail.banking.verified ? 'verified' : 'not verified'}
                          </span>
                        )}
                      </p>
                      {!detail.banking ? (
                        <p className="text-sm text-gray-400">{t('none_on_file')}</p>
                      ) : (
                        <dl className="text-sm space-y-1">
                          <Row k="Account holder" v={detail.banking.account_holder_name} />
                          <Row k="Bank" v={detail.banking.bank_name} />
                          <Row k="Country" v={detail.banking.country} />
                          <Row k="Currency" v={detail.banking.currency} />
                          <Row k="Account" v={detail.banking.account_number_last4} mono private_ />
                          <Row k="IBAN" v={detail.banking.iban_last4} mono private_ />
                          <Row k="SWIFT" v={detail.banking.swift_code} mono private_ />
                        </dl>
                      )}
                    </div>
                  </div>

                  {/* Onboarding progress */}
                  {detail.onboarding?.length > 0 && (
                    <div className="rounded-xl border border-gray-200 px-4 py-3">
                      <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">{t('onboarding')}</p>
                      <div className="flex flex-wrap gap-2">
                        {detail.onboarding.map(o => (
                          <span key={o.item_key}
                            className={`text-xs px-2.5 py-1 rounded-full border ${o.completed
                              ? 'bg-green-50 text-green-700 border-green-200'
                              : 'bg-gray-50 text-gray-500 border-gray-200'}`}>
                            {o.completed ? '✓' : '○'} {o.label}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Stat cards */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <Stat label="Projects" value={detail.stats.total_projects} />
                    <Stat label="Active" value={detail.stats.active_projects} />
                    <Stat label="Milestones done" value={`${detail.stats.milestones_completed}/${detail.stats.milestones_total}`} />
                    <Stat label="Earned (paid)" value={`$${(detail.stats.amount_earned || 0).toLocaleString()}`} />
                  </div>

                  {/* Charts */}
                  {detail.projects.length > 0 && (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                      <ProjectsByStatusPie projects={detail.projects} />
                      <BudgetUtilizationBar projects={detail.projects.map(p => ({ ...p, approved_budget: p.total_budget }))} />
                    </div>
                  )}

                  {/* Projects list */}
                  <div>
                    <p className="text-sm font-semibold text-gray-800 mb-2 flex items-center gap-1.5">
                      <FolderKanban className="w-4 h-4" /> Projects ({detail.projects.length})
                    </p>
                    {detail.projects.length === 0 ? (
                      <p className="text-sm text-gray-400">{t('not_assigned_projects')}</p>
                    ) : (
                      <div className="space-y-2">
                        {detail.projects.map(p => (
                          <div key={p.id} className="flex items-center justify-between gap-3 border border-gray-100 rounded-lg px-3 py-2">
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">{p.name}</p>
                              <p className="text-xs text-gray-500">
                                {p.project_number ? <span className="font-mono">{p.project_number} · </span> : ''}
                                {p.role_on_project} · {p.status}
                              </p>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              <div className="w-20 bg-gray-200 rounded-full h-1.5">
                                <div className="bg-indigo-500 h-1.5 rounded-full" style={{ width: `${p.progress || 0}%` }} />
                              </div>
                              <span className="text-xs text-gray-500 w-8 text-right">{p.progress || 0}%</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

const Row = ({ k, v, mono, private_ }) => (
  <div className="flex justify-between gap-3">
    <dt className="text-gray-500 shrink-0">{k}</dt>
    <dd className={`text-gray-900 text-right truncate ${mono ? 'font-mono' : ''}`} data-private={private_ || undefined}>
      {v || <span className="text-gray-300">&mdash;</span>}
    </dd>
  </div>
)

const Stat = ({ label, value }) => (
  <div className="bg-gray-50 border border-gray-100 rounded-lg p-3 text-center">
    <p className="text-lg font-bold text-gray-900">{value}</p>
    <p className="text-[11px] text-gray-500 mt-0.5">{label}</p>
  </div>
)
